/**
 * LocationMap — a single-pin Google map.
 *
 * The one component for "show this lat/lng on a map", used by the listing
 * location section, the address-step preview, and the About-page office map
 * (ADR 0142 unified the former keyless `<iframe>` onto this). Renders through
 * `<MapBoundary>` — the one component allowed to mount a Google map — so the
 * SDK loads on VIEW, not on mount (D13).
 *
 * Two loading modes, the caller's call:
 *   - `load="in-view"` (default): a FROZEN map — no gestures, no controls —
 *     that mounts once it comes within a viewport of the screen.
 *   - `load="tap"`: a placeholder with a "Show map" button; the tap opens an
 *     INTERACTIVE map in place. A frozen pin map is a picture, and a picture
 *     does not need the SDK — so the reader who wants the map asks for it.
 *
 * Layout:
 *   - default: a bordered, rounded container at `height` (300px).
 *   - `fill`:  fills its parent (`h-full w-full`, no own border/rounding) — for
 *     callers that already provide the framed container (e.g. an aspect box).
 */
import { MapBoundary, type MapLoad, type MapPlace } from '@/shared/components/map/MapBoundary';
import { AdvancedMarker } from '@/shared/components/map/vis-gl';
// Rust SSOT — (0,0) is the Gulf of Guinea; it (and NaN/out-of-range) means "no location".
import { DEFAULT_MAP_VIEWPORT, MAP_ZOOM_BUILDING } from '@letsgethome/lib/constants';
import { isValidCoordinates } from '@letsgethome/lib/wasm';
import { cn } from '@letsgethome/ui/lib/utils';
import { useMemo } from 'react';

// Hong Kong fallback when coordinates are absent/invalid — the Rust geo
// SSOT's canonical center; the default zoom is the single-building rung of
// the SSOT's semantic zoom ladder.
const DEFAULT_CENTER = { lat: DEFAULT_MAP_VIEWPORT.lat, lng: DEFAULT_MAP_VIEWPORT.lng };
const DEFAULT_ZOOM = MAP_ZOOM_BUILDING;

export interface LocationMapProps {
  /** Latitude of the pin. */
  lat: number;
  /** Longitude of the pin. */
  lng: number;
  /** Zoom level (default: the SSOT single-building zoom, street level). */
  zoom?: number;
  /** Container height when not `fill` (default '300px'). */
  height?: string;
  /** Fill the parent instead of using `height` + the default frame. */
  fill?: boolean;
  /** Extra classes on the container. */
  className?: string;
  /** Accessible name of the map and of its placeholder (e.g. "Office location"). */
  label: string;
  /** When the live map mounts (default `in-view`; see the header). */
  load?: MapLoad;
  /** The address/district shown if the Maps SDK cannot load (`MapBoundary`). */
  place?: MapPlace;
}

export function LocationMap({
  lat,
  lng,
  zoom = DEFAULT_ZOOM,
  height = '300px',
  fill = false,
  className,
  label,
  load = 'in-view',
  place,
}: LocationMapProps) {
  const center = useMemo(
    () => (isValidCoordinates(lat, lng) ? { lat, lng } : DEFAULT_CENTER),
    [lat, lng]
  );
  const hasValidLocation = isValidCoordinates(lat, lng);

  // A tapped map is one the reader ASKED for, so it is interactive; an
  // in-view one is the frozen picture it has always been.
  const interactive = load === 'tap';

  return (
    <div
      className={cn(
        'relative',
        fill ? 'h-full w-full' : 'overflow-hidden rounded-lg border border-border',
        className
      )}
      style={fill ? undefined : { height }}
      // The pin's ACTUAL position, on the container, so a test can assert
      // "the map followed the selection" against this component's own input
      // instead of scraping Google's tiles or its internal DOM. These are the
      // RESOLVED centre (the SSOT `isValidCoordinates` door has already
      // replaced an unrenderable pair with the Hong Kong fallback), so they
      // say where the map IS, not what it was asked for — and they are here
      // whether the boundary is still a placeholder or already the live map.
      data-map-lat={center.lat}
      data-map-lng={center.lng}
    >
      <MapBoundary
        load={load}
        label={label}
        pin={hasValidLocation ? center : undefined}
        place={place}
        map={{
          defaultCenter: center,
          defaultZoom: zoom,
          gestureHandling: interactive ? 'cooperative' : 'none',
          disableDefaultUI: !interactive,
        }}
      >
        {hasValidLocation && (
          <AdvancedMarker position={center}>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary shadow-lg">
              <div className="h-3 w-3 rounded-full bg-background" />
            </div>
          </AdvancedMarker>
        )}
      </MapBoundary>
    </div>
  );
}
